/**
 * Push notification service for subscribing and managing push notifications.
 */

import { pushAPI } from './api'

// Storage key for subscription status
const PUSH_ENABLED_KEY = 'push_notifications_enabled'

/**
 * Check if push notifications are supported in this browser.
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * Check if push notifications are enabled (user has subscribed).
 */
export function isPushEnabled(): boolean {
  return localStorage.getItem(PUSH_ENABLED_KEY) === 'true'
}

/**
 * Get current notification permission status.
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied'
  }
  return Notification.permission
}

/**
 * Request notification permission from the user.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied'
  }

  if (Notification.permission === 'granted') {
    return 'granted'
  }

  if (Notification.permission === 'denied') {
    return 'denied'
  }

  return await Notification.requestPermission()
}

/**
 * Convert a base64 string to Uint8Array (for VAPID key).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Subscribe to push notifications.
 *
 * This will:
 * 1. Request notification permission
 * 2. Get the VAPID public key from the server
 * 3. Subscribe via the PushManager API
 * 4. Send the subscription to our server
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('Push notifications are not supported')
    return false
  }

  try {
    // Request permission
    const permission = await requestNotificationPermission()
    if (permission !== 'granted') {
      console.log('Notification permission not granted')
      return false
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready

    // Check if already subscribed
    const existingSubscription = await registration.pushManager.getSubscription()
    if (existingSubscription) {
      // Already subscribed, send to server to ensure it's registered
      try {
        await pushAPI.subscribe(existingSubscription.toJSON())
        localStorage.setItem(PUSH_ENABLED_KEY, 'true')
        return true
      } catch (error) {
        console.error('Failed to sync existing subscription:', error)
        // Try to unsubscribe and resubscribe
        await existingSubscription.unsubscribe()
      }
    }

    // Get VAPID public key from server
    const { public_key } = await pushAPI.getVapidPublicKey()
    const applicationServerKey = urlBase64ToUint8Array(public_key)

    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    })

    // Send subscription to server
    await pushAPI.subscribe(subscription.toJSON())

    localStorage.setItem(PUSH_ENABLED_KEY, 'true')
    console.log('Successfully subscribed to push notifications')
    return true

  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error)
    localStorage.removeItem(PUSH_ENABLED_KEY)
    return false
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      // Notify server
      try {
        await pushAPI.unsubscribe(subscription.endpoint)
      } catch (error) {
        console.warn('Failed to unsubscribe on server:', error)
      }

      // Unsubscribe locally
      await subscription.unsubscribe()
    }

    localStorage.removeItem(PUSH_ENABLED_KEY)
    console.log('Successfully unsubscribed from push notifications')
    return true

  } catch (error) {
    console.error('Failed to unsubscribe from push notifications:', error)
    return false
  }
}

/**
 * Check and sync push subscription status.
 * Call this on app startup to ensure subscription is in sync.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!isPushSupported()) {
    return
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (subscription && isPushEnabled()) {
      // Re-send subscription to server to ensure it's registered
      try {
        await pushAPI.subscribe(subscription.toJSON())
      } catch (error) {
        console.warn('Failed to sync push subscription:', error)
      }
    } else if (!subscription && isPushEnabled()) {
      // Local storage says enabled but no subscription - clear state
      localStorage.removeItem(PUSH_ENABLED_KEY)
    }
  } catch (error) {
    console.error('Failed to sync push subscription:', error)
  }
}
